import { useCallback, useContext, useEffect, useMemo } from 'react';
import { orderBy } from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import type { FinancialStatementSmall as FinancialStatementVersion } from 'api';
import { notFoundPath } from 'common/config/urls';
import { PRIMARY_FINANCIAL_LABEL } from 'common/constants/financials/financials';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { toString } from 'utilities';

const useVersionsFilter = ({ financialVersions }: { financialVersions: FinancialStatementVersion[] }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location.search]);
  const versionQueryString = useMemo(() => {
    const { version } = parsedQuery;
    return Array.isArray(version) ? version[0] : version;
  }, [parsedQuery]);

  const { action: hasChanges, showDialog } = useContext(UnsavedChangesContext);

  const setQueryString = useCallback(
    (paramName: string, newQueryString: string | null | undefined) => {
      parsedQuery[paramName] = newQueryString ?? null;
      navigate({ pathname: location.pathname, search: queryString.stringify(parsedQuery) }, { replace: true });
    },
    [navigate, location.pathname, parsedQuery]
  );

  const defaultVersion = useMemo(
    () => financialVersions.find(version => !version.versioning),
    [financialVersions]
  );

  const matchVersion = useCallback(
    (version: string | number) =>
      financialVersions.find(item => item.slug === version || item.id?.toString() === version.toString()),
    [financialVersions]
  );

  const selectedVersion = useMemo(() => {
    if (!financialVersions.length) {
      return undefined;
    }

    const requestedVersion = versionQueryString || defaultVersion?.id;

    if (!requestedVersion) {
      return undefined;
    }

    return matchVersion(requestedVersion) ?? defaultVersion;
  }, [defaultVersion, financialVersions.length, matchVersion, versionQueryString]);

  // Keep invalid or id-based version URLs canonical without hiding the selector during hydration.
  useEffect(() => {
    if (!financialVersions.length) {
      return;
    }

    if (selectedVersion?.slug && versionQueryString !== selectedVersion.slug) {
      setQueryString('version', selectedVersion.slug);
      return;
    }

    if (!selectedVersion && !defaultVersion) {
      navigate(notFoundPath);
    }
  }, [defaultVersion, financialVersions.length, navigate, selectedVersion, setQueryString, versionQueryString]);

  const onVersionChange = useCallback(
    (versionId: string) => {
      const callback = () => {
        const tmpSelectedVersion = financialVersions.find(v => toString(v.id) === versionId);
        setQueryString('version', tmpSelectedVersion?.slug);
      };

      if (hasChanges) {
        showDialog(callback);
      } else {
        callback();
      }
    },
    [financialVersions, hasChanges, setQueryString, showDialog]
  );

  const orderedVersions = useMemo(() => {
    if (!financialVersions?.length) return [];
    return orderBy(financialVersions, 'versioning', 'desc').map(v => ({
      ...v,
      name: !v.versioning ? PRIMARY_FINANCIAL_LABEL : v.name,
    }));
  }, [financialVersions]);

  return { selectedVersion, orderedVersions, onVersionChange };
};

export default useVersionsFilter;
